import { Component, OnInit, Input, ViewChild } from '@angular/core';

import * as _ from 'lodash';

import { FirebaseService } from '../../services/firebase.service';
import { SnackBarService } from '../../services/snack-bar.service';

// import dictionaries
import { MeasurementAbbreviations, FractionSymbols } from '../../dictionaries';

import { Item } from '../../models/item';
import { Preferences } from '../../models/preferences';

@Component({
  selector    : 'item',
  templateUrl : 'item.html',
  styleUrls   : [ 'item.scss' ]
})

export class ItemComponent implements OnInit {
  @Input() item   : Item;
  @Input() isList : boolean;

  editing        : boolean = false;
  expanded       : boolean = false;
  toggleFraction : boolean = false;
  preferences    : Preferences;
  qty            : number;
  unit           : string;
  displayItem    : string;
  currentClasses : {};
  fractionValues : {};
  combinedItems;

  constructor(
    private firebaseService : FirebaseService,
    private snackBarService : SnackBarService
  ) {
    this.fractionValues = this.getFractionValues();
  }

  ngOnInit() : void {
    this.qty  = this.resetQty();
    this.unit = this.item.unit;
    this.displayItem = this.item.item;

    this.setCurrentClasses();
    this.initCombinedItems();

    this.firebaseService.preferences
      .take(1)
      .subscribe(pref => this.preferences = pref);
  }

  getFractionValues() : {} {
    const divider = '⁄',
      sup = ['⁰', '¹', '²', '³', '⁴', '⁵', '⁶', '⁷', '⁸', '⁹'],
      sub = ['₀', '₁', '₂', '₃', '₄', '₅', '₆', '₇', '₈', '₉'];

    let fractionValues : {} = _.invert(FractionSymbols);

    for (let i : number = 11; i < 100; i++) {
      const iStr : string = i.toString();

      fractionValues[1 / i] = `${sup[1]}${divider}${sub[iStr[0]]}${sub[iStr[1]]}`;
    }

    return fractionValues;
  }

  setCurrentClasses() {
    this.currentClasses = {
      'list-item'        : true,
      'recipe-list-item' : !this.isList,
      '--completed'      : this.item.completed,
      '--editing'        : this.editing,
      '--expanded'       : this.expanded
    };
  }

  resetQty() {
    return _.round(this.item.qty, 3);
  }

  initCombinedItems() : void {
    if (this.item.combinedItem)
      this.firebaseService.queryListItems({ key : 'combined', value : this.item.$key })
        .take(1)
        .subscribe(data => this.combinedItems = data);
  }

  abbr(unit : string) : string {
    return this.preferences.abbreviations ? MeasurementAbbreviations[unit] || unit : unit;
  }

  fraction(qty : number) : string {
    const stringQty : string = qty.toString();

    // only fractionalize if user wants that
    if (!this.preferences.fractions || this.toggleFraction)
      return stringQty;

    const integer : number = parseInt(stringQty, 10),
      decimal : number = this.decimal(qty);

    let result : string = '';

    if (integer)
      result += integer;

    if (decimal)
      result += this.fractionize(decimal);

    return result;
  }

  decimal(num : number) : number {
    return num - parseInt(num.toString(), 10);
  }

  fractionize(decimal : number) : string {
    // convert decimal to closest fraction in fraction values list
    const closest = _.keys(this.fractionValues).reduce((previous, current) => {
      return Math.abs(current - decimal) < Math.abs(previous - decimal) ? current : previous;
    });

    return this.fractionValues[closest];
  }

  toggleCompleted() {
    if (this.item.combinedItem)
      this.firebaseService.bulkUpdateItem(this.item, { completed : !this.item.completed });
    else
      this.firebaseService.updateItem(this.item, { completed : !this.item.completed });
  }

  edit() : void {
    this.editing = true;

    this.setCurrentClasses();
  }

  cancelEditing(): void {
    this.editing = false;

    this.qty = this.resetQty();
    this.unit = this.item.unit;
    this.displayItem = this.item.item;

    this.setCurrentClasses();
  }

  save() : void {
    const qty : number = parseFloat(this.qty + ''),
      unit : string = this.item.unit ? this.unit.trim() : null,
      item : string = this.displayItem.trim(),
      newQty : boolean = !isNaN(qty) && qty !== this.item.qty,
      newUnit : boolean = typeof unit === 'string' && unit !== this.item.unit,
      newItem : boolean = item.length && item !== this.item.item;

    // if nothing's changed, forget this ever happened
    if (!(newQty || newUnit || newItem))
      return this.cancelEditing();

    let changes = {};

    if (newQty)
      changes['qty'] = qty;

    if (newUnit)
      changes['unit'] = unit;

    if (newItem)
      changes['item'] = item;

    this.firebaseService.updateItem(this.item, changes);

    this.cancelEditing();
  }

  toggleExpand() : void {
    this.currentClasses['--expanded'] = this.expanded = !this.expanded;
  }

  delete() : void {
    this.firebaseService.updateItem(this.item, { deadManWalking : true });

    this.snackBarService.show({
      message         : 'You deleted item',
      emphasisMessage : this.displayItem,
      action          : 'UNDO'
    })
    .then(undo => {
      if (undo) {
        this.cancelEditing()

        this.firebaseService.updateItem(this.item, { deadManWalking : false });
      } else {
        this.firebaseService.removeItem(this.item);
      }
    });
  }
}


// WEBPACK FOOTER //
// /Users/briznad/Documents/WebDev/personal/cartmigo/cartmigo-dev/src/app/components/item/item.ts